import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CommonService } from 'src/app/shared/services/common.service';
import { CounterpartDetailService } from './counterpart-detail.service';
import { Subscription } from 'rxjs';
import {
  Counterpart,
  CounterpartBankAccount,
  CounterpartBankAccountForm,
  CounterpartContact,
  CounterpartContactForm,
  CounterpartDetailState,
  CounterpartForm,
  CounterpartOption,
  CounterpartVatId,
  CounterpartVatIdForm,
} from './counterpart-detail.model';

type CounterpartDetailSection = 'basic' | 'contacts' | 'vat' | 'bank' | 'address';

const DEFAULT_COUNTERPART_DETAIL_SECTIONS: Record<CounterpartDetailSection, boolean> = {
  basic: true,
  contacts: false,
  vat: false,
  bank: false,
  address: false,
};

@Component({
  selector: 'app-counterpart-side-draw',
  templateUrl: './counterpart-side-draw.component.html',
  styleUrls: ['./counterpart-detail.component.scss'],
})
export class CounterpartSideDrawComponent implements OnInit, OnDestroy {
  titles: string[] = [];
  sectionsOpen: Record<CounterpartDetailSection, boolean> = {
    ...DEFAULT_COUNTERPART_DETAIL_SECTIONS,
  };
  countries: CounterpartOption[] = [
    { name: 'Denmark', value: 'DK' },
    { name: 'Estonia', value: 'EE' },
    { name: 'France', value: 'FR' },
    { name: 'Germany', value: 'DE' },
    { name: 'Ireland', value: 'IE' },
    { name: 'Italy', value: 'IT' },
    { name: 'Luxembourg', value: 'LU' },
    { name: 'Netherlands', value: 'NL' },
    { name: 'Poland', value: 'PL' },
    { name: 'Singapore', value: 'SG' },
    { name: 'Spain', value: 'ES' },
    { name: 'Switzerland', value: 'CH' },
    { name: 'UK', value: 'GB' },
    { name: 'USA', value: 'US' },
  ]
  currencies: CounterpartOption[] = [{ name: 'Euro', value: 'EUR' }];
  vatTypes: CounterpartOption[] = [{ name: 'Value Added Tax (EU)', value: 'eu_vat' }];
  basicDetails: Record<string, string | undefined> = {};
  selectedCounterpart: Counterpart | null = null;
  selectedVatId: CounterpartVatId | null = null;
  selectedBankaccount: CounterpartBankAccount | null = null;
  selectedContact: CounterpartContact | null = null;
  selectedCounterpartAddress: Record<string, string> | null = null;
  address = {
    'Address line 1': '',
    'Address line 2': '',
    City: '',
    'Zip code': '',
    State: '',
    Country: '',
  };
  vatIds: CounterpartVatId[] = [];
  bankAccounts: CounterpartBankAccount[] = [];
  contacts: CounterpartContact[] = [];
  createCounterpart: CounterpartForm = {
    type: 'organization',
    name: undefined,
    phone: undefined,
    email: undefined,
    tax_id: undefined,
    street: undefined,
    pincode: undefined,
    city: undefined,
    state: undefined,
    country: undefined,
    addressLine1: undefined,
    addressLine2: undefined,
  };
  createVatId: CounterpartVatIdForm = {
    country: undefined,
    type: undefined,
    value: undefined,
  };
  createBankAccount: CounterpartBankAccountForm = {
    account_holder_name: undefined,
    bankName: undefined,
    accountNumber: undefined,
    country: undefined,
    currency: undefined,
    iban: undefined,
    bic: undefined,
    is_primary: false,
  };
  createContact: CounterpartContactForm = {
    firstName: undefined,
    lastName: undefined,
    phoneNumber: undefined,
    emailAddress: undefined,
    is_default: false,
  };
  @ViewChild('createCounterpartForm') createCounterpartForm!: NgForm;
  private counterpartDetailSubscription!: Subscription;
  creatingVatId = false;
  creatingBankAccount = false;
  creatingContact = false;
  creatingCounterpart = false;
  deletingCounterpart = false;
  isCreate = true;
  isAddContact = false;
  vehicleId: string;
  counterpartId: string;
  fetchingAddress = false;
  fetchingContacts = true;
  fetchingVatIds = false;
  fetchingBankAccounts = false;
  showContactForm = false;
  showBankAccountForm = false;
  showVatIdForm = false;
  editForm = false;
  private scrollTimer: ReturnType<typeof window.setTimeout> | null = null;

  constructor(
    public counterpartDetailService: CounterpartDetailService,
    private commonService: CommonService
  ) {}

  ngOnInit() {
    this.counterpartDetailSubscription =
      this.counterpartDetailService.counterpartDetailsObservable$.subscribe(
        (res: CounterpartDetailState | null) => {
          if (res && res.mode === 'detail' && res.id) {
            const counterpart = res as Counterpart;
            this.resetSections();
            this.selectedCounterpart = counterpart;
            //counterpart details
            this.isCreate = false;
            this.isAddContact = false;
            this.counterpartId = res.id;
            this.vehicleId = res.vehicleId;
            this.initDetailsSection(counterpart);
          } else if (res && res.mode === 'create') {
            // create counterpart
            this.resetSections();
            this.resetCreateCounterpartForm();
            this.isCreate = true;
            this.isAddContact = false;
            this.vehicleId = res.vehicleId;
            this.titles = [...['Create Counterparty']];
          } else if (res && res.mode === 'add-contact' && res.id) {
            // add-contact
            this.resetSections({ contacts: true });
            this.isCreate = false;
            this.isAddContact = true;
            this.resetCreateCounterpartForm();
            this.counterpartId = res.id;
            this.vehicleId = res.vehicleId;
            this.titles = [...['Add contact person']];
            this.createContact.is_default = true;
            this.scrollToTop();
            this.showContactForm = true;
          }
        }
      );
  }

  drawerTitle() {
    if (this.isCreate && !this.editForm) {
      return 'Create Counterparty';
    }
    if (this.isCreate && this.editForm) {
      return 'Edit Counterparty';
    }
    if (this.showContactForm) {
      return this.editForm ? 'Edit Contact' : 'Add Contact';
    }
    if (this.showVatIdForm) {
      return this.editForm ? 'Edit VAT ID' : 'Add VAT ID';
    }
    if (this.showBankAccountForm) {
      return this.editForm ? 'Edit Bank Account' : 'Add Bank Account';
    }
    return 'Counterparty Details';
  }

  drawerResetKey() {
    const mode = this.isCreate
      ? this.editForm ? 'edit-counterpart' : 'create-counterpart'
      : this.showContactForm
        ? this.editForm ? 'edit-contact' : 'add-contact'
        : this.showVatIdForm
          ? this.editForm ? 'edit-vat' : 'add-vat'
          : this.showBankAccountForm
            ? this.editForm ? 'edit-bank' : 'add-bank'
            : 'detail';
    return `${mode}-${this.counterpartId || this.selectedCounterpart?.id || ''}`;
  }

  isDetailView() {
    return !this.isCreate && !this.showContactForm && !this.showBankAccountForm && !this.showVatIdForm;
  }

  handleBackClick() {
    if (this.isCreate && this.editForm) {
      this.hideSubForms();
      return;
    }

    if (this.showContactForm || this.showVatIdForm || this.showBankAccountForm) {
      if (this.isAddContact) {
        this.counterpartDetailService.closeDrawer();
      } else {
        this.hideSubForms();
      }
      return;
    }

    this.counterpartDetailService.closeDrawer();
  }

  isSectionOpen(section: CounterpartDetailSection) {
    return this.sectionsOpen[section];
  }

  toggleSection(section: CounterpartDetailSection) {
    this.sectionsOpen[section] = !this.sectionsOpen[section];
  }

  resetSections(openSections: Partial<Record<CounterpartDetailSection, boolean>> = {}) {
    this.sectionsOpen = {
      ...DEFAULT_COUNTERPART_DETAIL_SECTIONS,
      ...openSections,
    };
  }

  counterpartName() {
    return this.selectedCounterpart?.name || this.createCounterpart.name || '-';
  }

  counterpartTypeLabel(type = this.selectedCounterpart?.type || this.createCounterpart.type) {
    return type === 'organization' ? 'Legal entity' : 'Private individual';
  }

  counterpartSlug() {
    return `counterparty_${(this.counterpartName() || '-').toString().replace(/\s+/g, '_')}`;
  }

  countryName(country?: string) {
    if (!country) return '-';
    return this.getCountry(country);
  }

  valueOrDash(value: unknown) {
    return value || '-';
  }

  createCounterpartHandler() {
    this.creatingCounterpart = true;
    this.counterpartDetailService
      .createCounterpart(this.vehicleId, {
        data: this.trimStringValues(this.createCounterpart),
      })
      .subscribe({
        next: response => {
          if (response) {
            this.counterpartId = response.id;
            this.selectedCounterpart = response;
            this.commonService.successNotification(
              'Counterparty created successfully'
            );
            this.counterpartDetailService.setCounterpartListRefresh(true);
            this.initDetailsSection(response);
          }
        },
        error: ({ error }) => {
          this.creatingCounterpart = false;
          if (error.message === 'Counterpart already exists') {
            this.commonService.infoNotification('Counterparty already exists');
          } else {
            this.commonService.errorNotification(
              'Failed to create counterparty'
            );
          }
        },
        complete: () => {
          this.creatingCounterpart = false;
        },
      });
  }

  createvatIdHandler() {
    this.creatingVatId = true;
    this.counterpartDetailService
      .createVatId(this.counterpartId, {
        data: this.trimStringValues(this.createVatId),
      })
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification('VAT Id added successfully');
            this.hideSubForms();
            this.getCounterpartVatIds();
          }
        },
        error: () => {
          this.creatingVatId = false;
          this.commonService.errorNotification('Failed to add VAT Id');
        },
        complete: () => {
          this.creatingVatId = false;
        },
      });
  }

  createBankAccountHandler() {
    this.creatingBankAccount = true;
    this.counterpartDetailService
      .createBankAccount(this.counterpartId, {
        data: this.trimStringValues(this.createBankAccount),
      })
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification(
              'Bank account added successfully'
            );

            this.hideSubForms();
            this.getCounterpartBankAccounts();
          }
        },
        error: ({ error }) => {
          this.creatingBankAccount = false;
          if (error.message === 'Bank Account already exists') {
            this.hideSubForms();
            this.commonService.infoNotification('Bank Account already exists');
          } else {
            this.commonService.errorNotification('Failed to add bank account');
          }
        },
        complete: () => {
          this.creatingBankAccount = false;
        },
      });
  }

  createContactHandler() {
    this.creatingContact = true;
    this.counterpartDetailService
      .createCounterpartContact(this.counterpartId, {
        data: this.trimStringValues(this.createContact),
      })
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification(
              'Contact person added successfully'
            );
            if (!this.isAddContact) {
              this.hideSubForms();
              this.getCounterpartContacts();
            } else {
              this.counterpartDetailService.closeDrawer();
            }
            this.counterpartDetailService.setCounterpartListRefresh(true);
          }
        },
        error: ({ error }) => {
          this.creatingContact = false;
          if (error.message === 'Contact person already exist') {
            !this.isAddContact && this.hideSubForms();
            this.commonService.infoNotification('Contact person already exist');
          } else {
            this.commonService.errorNotification(
              'Failed to add contact person'
            );
          }
        },
        complete: () => {
          this.creatingContact = false;
        },
      });
  }

  addContactHandler() {
    this.titles.push('Add contact person');
    this.resetContactForm();
    this.resetSections({ contacts: true });
    if (!this.contacts.length) {
      this.createContact.is_default = true;
    }
    this.scrollToTop();
    this.showContactForm = true;
  }

  addVatIdsHandler() {
    this.titles.push('Add VAT ID');
    this.resetVATIdForm();
    this.resetSections({ vat: true });
    this.scrollToTop();
    this.showVatIdForm = true;
  }

  addBankAccountHandler() {
    this.titles.push('Add bank account');
    this.resetBankaccountForm();
    this.resetSections({ bank: true });
    if (!this.bankAccounts.length) {
      this.createBankAccount.is_primary = true;
    }
    this.scrollToTop();
    this.showBankAccountForm = true;
  }

  getCounterpartBankAccounts() {
    this.fetchingBankAccounts = true;
    this.counterpartDetailService
      .getCounterpartBankAccounts(this.counterpartId)
      .subscribe({
        next: response => {
          const accounts = [...(response.data || [])].sort((a, b) =>
            (a.account_holder_name || '').localeCompare(
              b.account_holder_name || '',
              undefined,
              { numeric: true }
            )
          );
          this.bankAccounts = accounts;
        },
        error: () => {
          this.fetchingBankAccounts = false;
        },
        complete: () => {
          this.fetchingBankAccounts = false;
        },
      });
  }

  getCounterpartVatIds() {
    this.fetchingVatIds = true;
    this.counterpartDetailService
      .getCounterpartVatIds(this.counterpartId)
      .subscribe({
        next: response => {
          const vatIds = [...(response.data || [])].sort((a, b) =>
            (a.value || '').localeCompare(b.value || '', undefined, { numeric: true })
          );
          this.vatIds = vatIds.map(x => ({
            id: x.id,
            value: x.value,
            countryName: x.country ? this.getCountry(x.country) : undefined,
            country: x.country,
            type: x.type,
          }));
        },
        error: () => {
          this.fetchingVatIds = false;
        },
        complete: () => {
          this.fetchingVatIds = false;
        },
      });
  }

  getCountry(country: string) {
    const found = this.countries.find(c => c.value === country);
    return found ? found.name : country;
  }

  getCounterpartContacts() {
    this.fetchingContacts = true;
    this.counterpartDetailService
      .getCounterpartContacts(this.counterpartId)
      .subscribe({
        next: response => {
          const contacts = [...(response.data || [])].sort((a, b) =>
            `${a.firstName || ''}${a.lastName || ''}`.localeCompare(
              `${b.firstName || ''}${b.lastName || ''}`,
              undefined,
              { numeric: true }
            )
          );
          this.contacts = contacts;
        },
        error: () => {
          this.fetchingContacts = false;
        },
        complete: () => {
          this.fetchingContacts = false;
        },
      });
  }

  makePrimaryAccount(item: CounterpartBankAccount) {
    this.counterpartDetailService
      .makeDefaultBankaccount(this.counterpartId, item.id)
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification(
              'Default account changed successfully'
            );
            this.getCounterpartBankAccounts();
          }
        },
        error: () => {
          this.commonService.errorNotification(
            'Failed to change default account'
          );
        },
      });
  }

  makePrimaryContact(item: CounterpartContact) {
    this.counterpartDetailService
      .makeDefaultContact(this.counterpartId, item.id)
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification(
              'Default contact changed successfully'
            );
            this.getCounterpartContacts();
            this.counterpartDetailService.setCounterpartListRefresh(true);
          }
        },
        error: () => {
          this.commonService.errorNotification(
            'Failed to change default contact'
          );
        },
      });
  }

  editCounterpart() {
    if (!this.selectedCounterpart) {
      return;
    }

    this.resetCreateCounterpartForm();
    this.resetSections();
    this.editForm = true;
    const {
      type,
      name,
      phone,
      email,
      tax_id,
      street,
      pincode,
      city,
      state,
      country,
      addressLine1,
      addressLine2,
    } = this.selectedCounterpart;
    this.createCounterpart = {
      type: type || 'organization',
      name,
      phone,
      email,
      tax_id,
      street,
      pincode,
      city,
      state,
      country,
      addressLine1,
      addressLine2,
    };
    this.scrollToTop();
    this.isCreate = true;
  }

  editVatId(item: CounterpartVatId) {
    this.selectedVatId = item;
    this.resetVATIdForm();
    this.resetSections({ vat: true });
    this.editForm = true;
    this.createVatId = {
      value: item.value,
      country: item.country,
      type: item.type,
    };
    this.scrollToTop();
    this.showVatIdForm = true;
    this.titles.push(item.value || '');
  }

  editContact(item: CounterpartContact) {
    this.selectedContact = item;
    this.resetContactForm();
    this.resetSections({ contacts: true });
    this.editForm = true;
    const { firstName, lastName, phoneNumber, emailAddress, is_default } = item;
    this.createContact = {
      firstName,
      lastName,
      phoneNumber,
      emailAddress,
      is_default: is_default || false,
    };
    this.scrollToTop();
    this.showContactForm = true;
    this.titles.push(`${item.firstName} ${item.lastName}`);
  }

  editBankAccount(item: CounterpartBankAccount) {
    this.selectedBankaccount = item;
    this.resetBankaccountForm();
    this.resetSections({ bank: true });
    this.editForm = true;
    const {
      account_holder_name,
      bankName,
      accountNumber,
      country,
      currency,
      iban,
      bic,
      is_primary,
    } = item;
    this.createBankAccount = {
      account_holder_name,
      bankName,
      accountNumber,
      country,
      currency,
      iban,
      bic,
      is_primary: is_primary || false,
    };
    this.scrollToTop();
    this.showBankAccountForm = true;
    this.titles.push(item.account_holder_name || '');
  }

  updateCounterpartHandler() {
    this.creatingCounterpart = true;
    this.counterpartDetailService
      .updateCounterpart(this.vehicleId, this.counterpartId, {
        data: this.trimStringValues(this.createCounterpart),
      })
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification(
              'Counterparty updated successfully.'
            );
            this.selectedCounterpart = response;
            this.setCounterpartBasicDetails(response);
            this.hideSubForms();

            this.counterpartDetailService.setCounterpartListRefresh(true);
          }
        },
        error: ({ error }) => {
          this.creatingCounterpart = false;
          if (error.message === 'Counterpart already exists') {
            this.commonService.infoNotification('Counterparty already exists');
          } else {
            this.commonService.errorNotification(
              'Failed to update counterparty'
            );
          }
        },
        complete: () => {
          this.creatingCounterpart = false;
        },
      });
  }

  deleteCounterpart() {
    if (!this.vehicleId || !this.counterpartId || this.deletingCounterpart) {
      return;
    }

    this.deletingCounterpart = true;
    this.counterpartDetailService
      .deleteCounterpart(this.vehicleId, this.counterpartId)
      .subscribe({
        next: response => {
          if (response !== undefined) {
            this.commonService.successNotification(
              'Counterparty deleted successfully'
            );
            this.counterpartDetailService.setCounterpartListRefresh(true);
            this.counterpartDetailService.closeDrawer();
          }
        },
        error: ({ error }) => {
          this.deletingCounterpart = false;
          if (
            error?.message ===
            'Deletion failed because counterpart has associated invoices'
          ) {
            this.commonService.infoNotification(
              'Failed to delete Counterparty due to linked invoices'
            );
          } else {
            this.commonService.errorNotification('Failed to delete Counterparty');
          }
        },
        complete: () => {
          this.deletingCounterpart = false;
        },
      });
  }

  updateContactHandler() {
    if (!this.selectedContact?.id) {
      return;
    }

    this.creatingContact = true;
    this.counterpartDetailService
      .updateContact(this.counterpartId, this.selectedContact.id, {
        data: this.trimStringValues(this.createContact),
      })
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification(
              'Contact person updated successfully.'
            );

            this.hideSubForms();
            this.getCounterpartContacts();
            this.counterpartDetailService.setCounterpartListRefresh(true);
          }
        },
        error: ({ error }) => {
          this.creatingContact = false;
          if (error.message === 'Contact person already exist') {
            this.hideSubForms();
            this.commonService.infoNotification('Contact person already exist');
          } else {
            this.commonService.errorNotification(
              'Failed to update contact person'
            );
          }
        },
        complete: () => {
          this.creatingContact = false;
        },
      });
  }

  updateVatIdHandler() {
    if (!this.selectedVatId?.id) {
      return;
    }

    this.creatingVatId = true;
    this.counterpartDetailService
      .updateVatId(this.counterpartId, this.selectedVatId.id, {
        data: this.trimStringValues(this.createVatId),
      })
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification(
              'VAT Id updated successfully'
            );
            this.hideSubForms();
            this.getCounterpartVatIds();
          }
        },
        error: () => {
          this.creatingVatId = false;
          this.commonService.errorNotification('Failed to update VAT Id');
        },
        complete: () => {
          this.creatingVatId = false;
        },
      });
  }

  updateBankaccountHandler() {
    if (!this.selectedBankaccount?.id) {
      return;
    }

    this.creatingBankAccount = true;
    this.counterpartDetailService
      .updateBankAccount(this.counterpartId, this.selectedBankaccount.id, {
        data: this.trimStringValues(this.createBankAccount),
      })
      .subscribe({
        next: response => {
          if (response) {
            this.commonService.successNotification(
              'Bank account updated successfully'
            );

            this.hideSubForms();
            this.getCounterpartBankAccounts();
          }
        },
        error: ({ error }) => {
          this.creatingBankAccount = false;
          if (error.message === 'Bank Account already exists') {
            this.hideSubForms();
            this.commonService.infoNotification('Bank Account already exists');
          } else {
            this.commonService.errorNotification(
              'Failed to update bank account'
            );
          }
        },
        complete: () => {
          this.creatingBankAccount = false;
        },
      });
  }

  deleteVatId(vatId: string) {
    this.counterpartDetailService
      .deleteVatId(this.counterpartId, vatId)
      .subscribe({
        next: () => {
          this.deleteFromArray(this.vatIds, vatId);

          this.commonService.successNotification('VAT Id deleted successfully');
        },
        error: () => {
          this.commonService.errorNotification('Failed to delete VAT id');
        },
      });
  }

  deleteContact(contactId: string) {
    this.counterpartDetailService
      .deleteContact(this.counterpartId, contactId)
      .subscribe({
        next: () => {
          this.deleteFromArray(this.contacts, contactId);

          this.commonService.successNotification(
            'Contact deleted successfully'
          );
        },
        error: () => {
          this.commonService.errorNotification('Failed to delete contact');
        },
      });
  }

  deleteBankAccount(bankaccountId: string) {
    this.counterpartDetailService
      .deleteBankAccount(this.counterpartId, bankaccountId)
      .subscribe({
        next: () => {
          this.deleteFromArray(this.bankAccounts, bankaccountId);
          this.commonService.successNotification(
            'Bank account deleted successfully'
          );
        },
        error: ({ error }) => {
          const errors = Array.isArray(error?.error) ? error.error : [];
          const isLinkedToInvoices = errors.some(
            (message: string) => typeof message === 'string' && message.includes('foreign key constraint')
          );

          if (isLinkedToInvoices) {
            this.commonService.infoNotification(
              'Failed to delete bank account due to linked invoices'
            );
          } else {
            this.commonService.errorNotification(
              'Failed to delete bank account'
            );
          }
        },
      });
  }

  initDetailsSection(data: Counterpart) {
    this.setCounterpartBasicDetails(data);
    this.hideSubForms();
    this.getCounterpartContacts();
    this.getCounterpartVatIds();
    this.getCounterpartBankAccounts();
  }

  setCounterpartBasicDetails(res: Counterpart) {
    const titles: string[] = [];
    titles.push(res.name || '-');
    this.titles = [...titles];
    const basicDetails = {
      Category:
        res.type === 'organization' ? 'Legal entity' : 'Private Individual',
      Phone: res.phone,
      Email: res.email,
      Address: [res.addressLine1, res.addressLine2].filter(Boolean).join(', '),
      City: res.city,
      State: res.state,
      'Zip code': res.pincode,
      Country: res.country ? this.getCountry(res.country) : '',
    };
    if (res.type === 'organization') {
      this.basicDetails = { 'Company name': res.name, ...basicDetails };
    } else {
      this.basicDetails = {
        'Private Individual Name': res.name,
        ...basicDetails,
      };
    }
  }

  convertObjectToArray(
    obj: Record<string, unknown>
  ): { key: string; value: unknown }[] {
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  hideSubForms() {
    this.titles.length = 1;
    this.isCreate = false;
    this.showContactForm = false;
    this.showVatIdForm = false;
    this.showBankAccountForm = false;
    this.editForm = false;
    this.scrollToTop();
  }

  deleteFromArray<T extends { id: string }>(array: T[], id: string) {
    const index = array.findIndex(item => item.id === id);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  scrollToTop() {
    this.clearScrollTimer();
    this.scrollTimer = window.setTimeout(() => {
      document
        .querySelector('.counterpart-details-side-nav .portal-detail-drawer__card')
        ?.scrollTo({ top: 0 });
      this.scrollTimer = null;
    });
  }

  resetCreateCounterpartForm() {
    this.createCounterpart = {
      type: 'organization',
      name: undefined,
      phone: undefined,
      email: undefined,
      tax_id: undefined,
      street: undefined,
      pincode: undefined,
      city: undefined,
      state: undefined,
      country: undefined,
      addressLine1: undefined,
      addressLine2: undefined,
    };
  }

  resetBankaccountForm() {
    this.createBankAccount = {
      account_holder_name: undefined,
      accountNumber: undefined,
      bankName: undefined,
      country: undefined,
      currency: undefined,
      iban: undefined,
      bic: undefined,
      is_primary: false,
    };
  }

  resetVATIdForm() {
    this.createVatId = {
      country: undefined,
      type: undefined,
      value: undefined,
    };
  }

  resetContactForm() {
    this.createContact = {
      firstName: undefined,
      lastName: undefined,
      phoneNumber: undefined,
      emailAddress: undefined,
      is_default: false,
    };
  }

  ngOnDestroy() {
    this.clearScrollTimer();
    if (this.counterpartDetailSubscription) {
      this.counterpartDetailSubscription.unsubscribe();
    }
  }

  private clearScrollTimer(): void {
    if (this.scrollTimer) {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
    }
  }

  trimStringValues<T extends object>(obj: T): T {
    const trimmed = { ...obj };

    (Object.keys(trimmed) as Array<keyof T>).forEach(typedKey => {
      const value = trimmed[typedKey];
      if (typeof value === 'string') {
        trimmed[typedKey] = value.trim() as T[keyof T];
      }
    });

    return trimmed;
  }
}
